import { useMutation } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { updateProfile } from "src/api/settings";
import { TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useAuth } from "src/providers/AuthProvider";
import { showSuccessToast } from "src/lib/utils";
import type { AuthUser } from "src/types/auth";

const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("This field is required"),
	last_name: Yup.string().required("This field is required"),
	phone: Yup.string().required("This field is required"),
});

interface ProfileFormValues {
	uuid?: string;
	first_name: string;
	last_name: string;
	phone: string;
	image: File | null;
}

interface EditProfileModalProps {
	user: AuthUser;
}

const EditProfileModal = ({ user }: EditProfileModalProps) => {
	const closePortal = usePortalContext();
	const { setUser } = useAuth();

	const initialValues: ProfileFormValues = {
		uuid: user?.uuid,
		first_name: user.first_name || "",
		last_name: user.last_name || "",
		phone: user.phone || "",
		image: null,
	};

	const { mutate, isLoading } = useMutation(updateProfile, {
		onSuccess: ({ message, data }) => {
			showSuccessToast(message ?? "");
			setUser(data ?? null);
			closePortal();
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="First name"
							name="first_name"
							placeholder="First name"
							value={values.first_name}
							error={touched.first_name && errors.first_name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="Last name"
							name="last_name"
							placeholder="Last name"
							value={values.last_name}
							error={touched.last_name && errors.last_name}
							onChange={handleChange}
							required
						/>
					</div>

					<TextField
						label="Contact"
						name="phone"
						placeholder="Phone number"
						value={values.phone}
						error={touched.phone && errors.phone}
						onChange={handleChange}
						required
					/>

					<TextField
						label="Profile image"
						name="image"
						type="file"
						accept="image/*"
						hint="Leave empty to keep your current photo"
						onChange={(event) => setFieldValue("image", event.currentTarget.files?.[0] ?? null)}
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Updating..." : "Update profile"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default EditProfileModal;
